import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { getMasters, createMovement, MOVEMENT_TYPES, DELIVERY_MODES, errorMessage } from '../services/api';
import { toast } from 'sonner';
import { useFieldErrors } from '../lib/useFieldErrors';
import { FieldError } from '../components/FieldError';
import { MasterPicker } from '../components/MasterPicker';
import { ArrowLeft, Loader2, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../lib/utils';

export default function MovementForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const preselectedMasterId = searchParams.get('master_id');
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [masters, setMasters] = useState([]);
    const { errors, fail, clearError, fieldProps } = useFieldErrors();
    const [formData, setFormData] = useState({
        master_id: preselectedMasterId || '',
        type: 'reception',
        date: new Date(),
        reception_address: '',
        expedition_address: '',
        delivery_mode: '',
        tracking_number: '',
        notes: ''
    });

    useEffect(() => {
        fetchMasters();
    }, []);

    const fetchMasters = async () => {
        try {
            const response = await getMasters();
            setMasters(response.data);
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement des masters'));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (date) => {
        if (date) {
            setFormData(prev => ({ ...prev, date }));
        }
    };

    const goBack = () => {
        if (preselectedMasterId) {
            navigate(`/masters/${preselectedMasterId}`);
        } else {
            navigate('/movements');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.master_id) {
            fail('f-master', 'Veuillez sélectionner un master');
            return;
        }

        setSaving(true);

        try {
            const data = {
                ...formData,
                date: format(formData.date, 'yyyy-MM-dd')
            };
            await createMovement(data);
            toast.success('Mouvement créé');
            goBack();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la création'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="movement-form">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon" aria-label="Retour" 
                        onClick={goBack}
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">
                        NOUVEAU MOUVEMENT
                    </h1>
                </div>

                {/* Form */}
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <form noValidate onSubmit={handleSubmit} className="space-y-6">
                            {/* Master Selection */}
                            <div className="space-y-2">
                                <Label htmlFor="f-master">Master *</Label>
                                <MasterPicker
                                    id="f-master"
                                    masters={masters}
                                    value={formData.master_id}
                                    onChange={(v) => { handleSelectChange('master_id', v); clearError('f-master'); }}
                                    invalidProps={fieldProps('f-master')}
                                />
                                <FieldError id="f-master" message={errors['f-master']} />
                            </div>

                            {/* Type & Date */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="f-type">Type *</Label>
                                    <Select 
                                        value={formData.type} 
                                        onValueChange={(v) => handleSelectChange('type', v)}
                                    >
                                        <SelectTrigger id="f-type" data-testid="type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(MOVEMENT_TYPES).map(([key, label]) => (
                                                <SelectItem key={key} value={key}>{label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="f-date">Date *</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button id="f-date"
                                                variant="outline"
                                                className={cn(
                                                    "w-full justify-start text-left font-normal",
                                                    !formData.date && "text-muted-foreground"
                                                )}
                                                data-testid="date-picker"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {formData.date ? format(formData.date, 'dd MMMM yyyy', { locale: fr }) : "Sélectionner"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={formData.date}
                                                onSelect={handleDateChange}
                                                initialFocus
                                                locale={fr}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>

                            {/* Addresses */}
                            {formData.type === 'reception' && (
                                <div className="space-y-2">
                                    <Label htmlFor="reception_address">Adresse de réception</Label>
                                    <Textarea
                                        id="reception_address"
                                        name="reception_address"
                                        value={formData.reception_address}
                                        onChange={handleChange}
                                        placeholder="Adresse de provenance"
                                        rows={2}
                                        data-testid="reception-address-input"
                                    />
                                </div>
                            )}

                            {formData.type === 'expedition' && (
                                <div className="space-y-2">
                                    <Label htmlFor="expedition_address">Adresse d'expédition</Label>
                                    <Textarea
                                        id="expedition_address"
                                        name="expedition_address"
                                        value={formData.expedition_address}
                                        onChange={handleChange}
                                        placeholder="Adresse de destination"
                                        rows={2}
                                        data-testid="expedition-address-input"
                                    />
                                </div>
                            )}

                            {formData.type === 'transfert' && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="reception_address">Adresse de départ</Label>
                                        <Textarea
                                            id="reception_address"
                                            name="reception_address"
                                            value={formData.reception_address}
                                            onChange={handleChange}
                                            placeholder="Adresse de départ"
                                            rows={2}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="expedition_address">Adresse d'arrivée</Label>
                                        <Textarea
                                            id="expedition_address"
                                            name="expedition_address"
                                            value={formData.expedition_address}
                                            onChange={handleChange}
                                            placeholder="Adresse d'arrivée"
                                            rows={2}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Delivery Mode & Tracking */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="f-mode-de-livraison">Mode de livraison</Label>
                                    <Select 
                                        value={formData.delivery_mode} 
                                        onValueChange={(v) => handleSelectChange('delivery_mode', v)}
                                    >
                                        <SelectTrigger id="f-mode-de-livraison" data-testid="delivery-mode-select">
                                            <SelectValue placeholder="Sélectionner" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(DELIVERY_MODES).map(([key, label]) => (
                                                <SelectItem key={key} value={key}>{label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="tracking_number">Numéro de suivi</Label>
                                    <Input
                                        id="tracking_number"
                                        name="tracking_number"
                                        value={formData.tracking_number}
                                        onChange={handleChange}
                                        placeholder="Ex: 1Z999AA10123456784"
                                        data-testid="tracking-input"
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes</Label>
                                <Textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="Notes supplémentaires..."
                                    rows={3}
                                    data-testid="notes-input"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={goBack}
                                    className="flex-1"
                                >
                                    Annuler
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1"
                                    data-testid="submit-button"
                                >
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Créer le mouvement
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
